import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError, type U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { doInitLU, luHandleAppiError } from "./U2103Service";
import { U2103ContextProvider } from "./U2103Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

export const NS = "u2103";

export const U2103ROLES = {
  READ: "U2103:READ",
  ADD: "U2103:ADD",
  EDIT: "U2103:EDIT",
  DELETE: "U2103:DELETE",
  ROLES: "U2103:ROLES",
  ACTIONS: "U2103:ACTIONS",
  AUDITS: "U2103:AUDITS",
  RESETPASSWORD: "U2103:RESETPASSWORD",
  VIEWSECRETKEY: "U2103:VIEWSECRETKEY",
  EXPORT: "U2103:EXPORT",
} as const;
export type U2103ROLES = (typeof U2103ROLES)[keyof typeof U2103ROLES];

export interface U2103InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
}

const U2103: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U2103InitLU>({ launchSearch: false, hideSearch: true, nbRecordPerPage: 50 });
  // charge fichiers de traduction
  useEffect(() => {
    i18next.loadNamespaces(["u2103"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  // Gestion de l'initialisation de l'unité logique
  const handleInitLU = useU2000InitLUHandlers<U2103InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U2103InitLU) => {
      setInitLUData(initLU);
      setInitLUReady(true);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  // Initialisation de l'unité logique au montage du composant et apres quelques changements de langue
  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]);

  // Set LU title and breadcrumb
  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U2103ContextProvider initLU={initLUData}>
      <Outlet />
    </U2103ContextProvider>
  );
};

export default U2103;
